import { useCallback, useEffect, useRef } from "react";
import { SchedulesReportLayout } from "@common/types/reports/schedules-report";
import {
    CommandsDirectory,
    InjectedCommandOption,
    ScheduleApplicationBulkLifecycleOption,
} from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import { getUniqueScheduleApplicationIds } from "./scheduleApplicationRowId";
import {
    buildSelectedSchedulesReportUrl,
    getReportableScheduleRows,
} from "./scheduleApplicationsReportLink";

export function useScheduleApplicationBulkCommandActions(options: {
    selectedRows: ScheduleApplicationParentRow[];
    periodStart: string | null;
    periodEnd: string | null;
    onOpenReport: (url: string) => void;
    onMarkBilled?: () => void;
    markBilledDisabledReason?: string;
    /** Pending milestone rows selected under the schedules; zero when the flag is off. */
    selectedMilestoneRowCount?: number;
    onMilestoneAchieved?: () => void;
    milestoneAchievedDisabledReason?: string;
    lifecycleActions?: ScheduleApplicationBulkLifecycleOption[];
    changePolicyOptions?: InjectedCommandOption[];
    changePolicyDisabledReason?: string;
}) {
    const {
        selectedRows,
        periodStart,
        periodEnd,
        onOpenReport,
        onMarkBilled,
        markBilledDisabledReason,
        selectedMilestoneRowCount = 0,
        onMilestoneAchieved,
        milestoneAchievedDisabledReason,
        lifecycleActions,
        changePolicyOptions,
        changePolicyDisabledReason,
    } = options;
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const handleOpenBulkActions = useCallback(() => {
        const hasScheduleRows = selectedRows.length > 0;
        if (!hasScheduleRows && selectedMilestoneRowCount === 0) {
            return;
        }
        const openReport = (layout: SchedulesReportLayout) => () => {
            const url = buildSelectedSchedulesReportUrl({
                selectedRows,
                layout,
                periodStart,
                periodEnd,
            });
            closeCommands();
            if (url) {
                onOpenReport(url);
            }
        };
        const selectedScheduleApplicationCount =
            getUniqueScheduleApplicationIds(
                getReportableScheduleRows(selectedRows),
            ).length;
        openCommands(CommandsDirectory.SCHEDULE_APPLICATION_BULK, {
            selectedScheduleApplicationCount,
            selectedScheduleMilestoneCount: selectedMilestoneRowCount,
            ...(hasScheduleRows && {
                onScheduleApplicationsOpenRollforward: openReport(
                    SchedulesReportLayout.rollforward,
                ),
                onScheduleApplicationsOpenWaterfall: openReport(
                    SchedulesReportLayout.waterfall,
                ),
            }),
            ...(hasScheduleRows &&
                onMarkBilled && {
                    onScheduleApplicationsMarkBilled: () => {
                        closeCommands();
                        onMarkBilled();
                    },
                    scheduleApplicationsMarkBilledDisabledReason:
                        markBilledDisabledReason,
                }),
            ...(onMilestoneAchieved && {
                onScheduleApplicationsMilestoneAchieved: () => {
                    closeCommands();
                    onMilestoneAchieved();
                },
                scheduleApplicationsMilestoneAchievedDisabledReason:
                    milestoneAchievedDisabledReason,
            }),
            ...(hasScheduleRows &&
                changePolicyOptions && {
                    scheduleApplicationsChangePolicyOptions:
                        changePolicyOptions,
                    scheduleApplicationsChangePolicyDisabledReason:
                        changePolicyDisabledReason,
                    scheduleApplicationsChangePolicySelectedCount:
                        getUniqueScheduleApplicationIds(selectedRows).length,
                }),
            ...(hasScheduleRows &&
                lifecycleActions && {
                    scheduleApplicationsLifecycleActions: lifecycleActions.map(
                        (action) => ({
                            ...action,
                            onSelect: () => {
                                closeCommands();
                                action.onSelect();
                            },
                        }),
                    ),
                }),
        });
    }, [
        changePolicyDisabledReason,
        changePolicyOptions,
        closeCommands,
        lifecycleActions,
        markBilledDisabledReason,
        milestoneAchievedDisabledReason,
        onMarkBilled,
        onMilestoneAchieved,
        onOpenReport,
        openCommands,
        periodEnd,
        periodStart,
        selectedMilestoneRowCount,
        selectedRows,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    const hasSelection =
        selectedRows.length > 0 || selectedMilestoneRowCount > 0;
    useEffect(() => {
        if (!hasSelection) {
            return undefined;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);

    return { handleOpenBulkActions };
}
